import {
  getStoreCode,
  getCountryCode,
  getDefaultLanguage,
  getDefaultSizeSchema,
  setBrowseCurrency as setReduxBrowseCurrency,
  setKeyStoreDataversion as setReduxKeyStoreDataversion,
  getCurrencies,
  getStore as getRegionalStore,
  getRegionalStoreVariant,
  getStoreUrlForMen,
  getStoreUrlForWomen,
  getDefaultStoreUrl
} from "../../template/state/modules/regionalStore";
import {
  setBrowseCountry,
  setBrowseCurrency,
  setBrowseLanguage,
  setBrowseSizeSchema,
  setStoreCode,
  getGeoCountry,
  setKeyStoreDataversion,
  getKeyStoreDataversion,
  setCurrency
} from "@asosteam/asos-web-store-presentation-node";
import {
  checkForMismatchAndSetCookie,
  correctUndefinedCookie
} from "./checkForMismatchAndSetCookie";
import getters from "./getters";
import { getAllKeyStoreDataversions } from "./getAllVersions";
import {
  manifestWindowKey,
  asosGlobalNamespace,
  siteChromeGlobalNamespace,
  siteChromeTokenValuePlatform,
  siteChromeTokenValueGender,
  siteChromeTokenValues,
  siteChromeVersionCookieName
} from "../../template/constants";
import { isFragmentationMode } from "../../template/components/TemplateFragmentation/isFragmentationMode";
import { getCurrency } from "../../helpers/currency";
import { setCookieValue } from "./cookies";
import redirectToPreferredSite from "../redirectToPreferredSite";
import templateMatchesCountry from "./templateMatchesCountry";
import geoCountryMapsToCurrentStore from "./geoCountryMapsToCurrentStore";
import sanitiseFloor from "./sanitiseFloor";
import getTemplateKey from "./getTemplateKey";
import { getParamValue, isWindowDefined } from "../../helpers/url";
import { handleAffiliateId } from "./handleAffiliateId";
import { finishSettingCustomAttributeFromPreviousRequest } from "@src/helpers/monitoring";
import getTemplateVersion from "./getTemplateVersion";
import { getLegacySiteChromeVersion } from "./getLegacySiteChromeVersion";

const customAttributeNameForUndefinedKSDVCookie =
  "UndefinedKeyStoreDataversionCookieWasCorrected";

const getBrowseCurrency = currencies => {
  const { currency, currencyId } = getCurrency(
    parseInt(getParamValue("currencyId")),
    getParamValue("browseCurrency"),
    currencies
  );
  return {
    browseCurrency: currency,
    browseCurrencyId: currencyId
  };
};

const getSharedBaseContext = ({
  currencies,
  defaultLanguage,
  defaultSizeSchema,
  storeCode
}) => ({
  ...getBrowseCurrency(currencies),
  browseLanguage: defaultLanguage,
  browseSizeSchema: defaultSizeSchema,
  storeId: storeCode
});

const getBase = ({
  storeUrlForMen,
  storeUrlForWomen,
  defaultStoreUrl,
  browseCountry,
  regionalStoreVariant,
  platform,
  floor,
  templateKey,
  templateVersion,
  ...regionalStoreFromState
}) => {
  const affiliateId = handleAffiliateId([
    storeUrlForMen,
    storeUrlForWomen,
    defaultStoreUrl
  ]);
  const geoCountry = getGeoCountry() || browseCountry;
  const base = {
    affiliateId,
    browseCountry: browseCountry && browseCountry.toUpperCase(),
    enableStoreBasedOperation: false,
    geoCountry,
    platform,
    floor,
    templateKey,
    templateVersion,
    ...getSharedBaseContext(regionalStoreFromState)
  };

  if (regionalStoreVariant) {
    return {
      ...base,
      ...getSharedBaseContext(regionalStoreVariant)
    };
  }

  return base;
};

const stubContextForRedirectedPages = {
  pendingRedirect: true
};

const buildContextObject = (store, keyStoreDataversion, latestManifest) => {
  let contextObject = Object.assign(
    {},
    {
      keyStoreDataversion,
      templateKey: getTemplateKey(),
      templateVersion: isFragmentationMode
        ? getTemplateVersion()
        : store.getState().version
    }
  );

  const updateRegionalStore = callback => {
    // update the keystoredataversion in state
    store.dispatch(
      setReduxKeyStoreDataversion(contextObject.keyStoreDataversion)
    );
    // update browseCurrency in state
    store.dispatch(setReduxBrowseCurrency(contextObject.browseCurrency));

    if (callback) {
      callback();
    }
  };

  const state = store.getState();
  const storeCode = getStoreCode(state);

  const base = getBase({
    browseCountry: getCountryCode(state),
    storeCode,
    storeUrlForMen: getStoreUrlForMen(state),
    storeUrlForWomen: getStoreUrlForWomen(state),
    defaultStoreUrl: getDefaultStoreUrl(state),
    defaultLanguage: getDefaultLanguage(state),
    defaultSizeSchema: getDefaultSizeSchema(state),
    currencies: getCurrencies(state),
    regionalStoreVariant: getRegionalStoreVariant(
      getRegionalStore(state),
      contextObject.keyStoreDataversion
    ),
    platform:
      window[asosGlobalNamespace][siteChromeGlobalNamespace][
        siteChromeTokenValues
      ][siteChromeTokenValuePlatform],
    floor: sanitiseFloor(
      window[asosGlobalNamespace][siteChromeGlobalNamespace][
        siteChromeTokenValues
      ][siteChromeTokenValueGender]
    ),
    templateKey: getTemplateKey()
  });

  contextObject = {
    ...base,
    ...contextObject,
    buildVersion: state.buildVersion
  };

  if (!templateMatchesCountry(contextObject)) {
    if (geoCountryMapsToCurrentStore(getGeoCountry(), latestManifest)) {
      setBrowseCountry(getGeoCountry());
      window.location.reload();

      return stubContextForRedirectedPages;
    }
  }

  // Set new page cookies
  setCookies(contextObject);

  // Remove browse parameters from URL
  const url = ["browseCurrency", "browseCountry"].reduce(
    (current, value) => removeURLParameter(current, value),
    window.location.href
  );
  window.history.replaceState({}, "", url);

  // create context getters object, the external interface
  const contextGetters = getters(contextObject);

  return { context: contextGetters, updateRegionalStore };
};

const oneHour = 1 / 24;
const fiveMinutes = oneHour / 12;

const mustReload = keyStoreDataversions => {
  const setKeyStoreDataVersionCookie = setKeyStoreDataversion.bind(
    null,
    keyStoreDataversions.manifestValue
  );

  return (
    correctUndefinedCookie(
      keyStoreDataversions,
      setKeyStoreDataVersionCookie,
      customAttributeNameForUndefinedKSDVCookie
    ) ||
    checkForMismatchAndSetCookie(
      keyStoreDataversions,
      setKeyStoreDataVersionCookie
    )
  );
};

const setSiteChromeVersionCookie = value => {
  setCookieValue(siteChromeVersionCookieName, value, {
    expires: fiveMinutes
  });
};

const updateCookieExpiry = (state, versionManifest) => {
  if (isFragmentationMode) {
    setSiteChromeVersionCookie(getTemplateVersion());
  } else {
    setSiteChromeVersionCookie(
      getLegacySiteChromeVersion(state, versionManifest)
    );
  }

  setKeyStoreDataversion(getKeyStoreDataversion());
};

export function context(store) {
  const latestManifest = window[asosGlobalNamespace][manifestWindowKey];
  const state = store.getState();

  const keyStoreDataversions = getAllKeyStoreDataversions(
    state,
    latestManifest
  );

  const keyStoreDataversion =
    keyStoreDataversions.cookieValue || keyStoreDataversions.manifestValue;

  if (redirectToPreferredSite(latestManifest, state, keyStoreDataversion)) {
    return stubContextForRedirectedPages;
  }

  const shouldReload = mustReload(keyStoreDataversions);

  updateCookieExpiry(state, latestManifest);

  if (shouldReload) {
    window.location.reload();
  } else {
    finishSettingCustomAttributeFromPreviousRequest(
      customAttributeNameForUndefinedKSDVCookie
    );
  }

  return buildContextObject(store, keyStoreDataversion, latestManifest);
}

function removeURLParameter(url, parameter) {
  const urlParts = url.split("?");
  if (urlParts.length >= 2) {
    const prefix = encodeURIComponent(parameter) + "=";
    const parts = urlParts[1].split(/[&;]/g);

    for (let i = parts.length; i-- > 0;) {
      if (parts[i].lastIndexOf(prefix, 0) !== -1) {
        parts.splice(i, 1);
      }
    }

    return urlParts[0] + (parts.length > 0 ? "?" + parts.join("&") : "");
  } else {
    return url;
  }
}

/*
 * @param {Object} webContext
 * @param {string} webContext.browseCountry
 * @param {string} webContext.browseCurrency
 * @param {string} webContext.browseLanguage
 * @param {string} webContext.browseSizeSchema
 * @param {string} webContext.storeId
 * @param {string} webContext.browseCurrencyId
 */
export function setCookies(webContext) {
  // true represents session cookie
  setBrowseCountry(webContext.browseCountry);
  setBrowseCurrency(webContext.browseCurrency);
  setBrowseLanguage(webContext.browseLanguage);
  setBrowseSizeSchema(webContext.browseSizeSchema);
  setStoreCode(webContext.storeId);
  setCurrency({
    currencyCode: webContext.browseCurrency,
    currencyId: webContext.browseCurrencyId
  });
}

export function getContext() {
  return (
    isWindowDefined() &&
    window[asosGlobalNamespace] !== undefined &&
    window[asosGlobalNamespace].webContext
  );
}
